import React, { useEffect, useMemo, useRef, useState } from "react";
import Select, { InputActionMeta, type StylesConfig } from "react-select";
import { Calendar, ChevronDown, Filter, X } from "lucide-react";
import {
	commonListingFilterSelectStyles,
	type CommonListingFilterOption,
} from "util/selectStyles";

export interface CommonFilterOption {
	value: string;
	label: string;
}

interface BaseField {
	id: string;
	label?: string;
	placeholder?: string;
	widthClass?: string;
	queryParam?: string;
	disabled?: boolean;
	isActive?: boolean;
	isRequired?: boolean;
	errorMessage?: string;
}

export interface TextFilterField extends BaseField {
	type: "text";
	value: string;
	onChange: (value: string) => void;
}

export interface DateFilterField extends BaseField {
	type: "date";
	value: string;
	min?: string;
	max?: string;
	onChange: (value: string) => void;
}

export interface NativeSelectFilterField extends BaseField {
	type: "native-select";
	value: string;
	options: CommonFilterOption[];
	onChange: (value: string) => void;
}

export interface SelectFilterField extends BaseField {
	type: "select";
	value: CommonFilterOption | null;
	options: CommonFilterOption[];
	isClearable?: boolean;
	isSearchable?: boolean;
	isLoading?: boolean;
	isDisabled?: boolean; // Added to support disabling mutually exclusive filters
	noOptionsMessage?: (obj: { inputValue: string }) => string;
	onInputChange?: (value: string, meta: InputActionMeta) => string;
	onMenuScrollToBottom?: () => void;
	menuPortalTarget?: HTMLElement;
	onChange: (value: CommonFilterOption | null) => void;
}

export type CommonFilterField =
	| TextFilterField
	| DateFilterField
	| NativeSelectFilterField
	| SelectFilterField;

interface CommonListingFilterProps {
	fields: CommonFilterField[];
	filterButtonLabel?: string;
	panelTitle?: string;
	className?: string;
	panelAlign?: "left" | "right";
	enforceGrayBorders?: boolean;
	onApply?: () => void | boolean;
	onClear?: () => void;
	onReset?: () => void | boolean;
	onClose?: () => void;
	defaultExpanded?: boolean;
	disableApplyOnEmpty?: boolean;
}

const baseInputClassName =
	"listing-filter-field h-[38px] w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100 text-sm px-2.5 py-2 focus:ring-1 focus:ring-slate-300 dark:focus:ring-slate-600 focus:border-slate-300 dark:focus:border-slate-600 disabled:cursor-not-allowed disabled:text-slate-400 dark:disabled:text-slate-500";

const hasValue = (field: CommonFilterField) => {
	if (typeof field.isActive === "boolean") {
		return field.isActive;
	}

	if (field.type === "select") {
		return Boolean(field.value?.value);
	}
	return Boolean(field.value);
};

const CommonListingFilter: React.FC<CommonListingFilterProps> = ({
	fields,
	filterButtonLabel = "Filter",
	panelTitle = "Listing Filter",
	className,
	panelAlign = "left",
	enforceGrayBorders = true,
	onApply,
	onClear,
	onReset,
	onClose,
	defaultExpanded = false,
	disableApplyOnEmpty = false,
}) => {
	const [isOpen, setIsOpen] = useState(defaultExpanded);
	const containerRef = useRef<HTMLDivElement>(null);
	const prevIsOpen = useRef(isOpen);

	const hasActiveFilters = useMemo(
		() => fields.some((field) => hasValue(field)),
		[fields],
	);

	const selectStyles = useMemo<StylesConfig<CommonListingFilterOption, false>>(() => {
		return commonListingFilterSelectStyles;
	}, []);

	useEffect(() => {
		if (prevIsOpen.current === true && isOpen === false) {
			if (onClose) {
				onClose();
			}
		}
		prevIsOpen.current = isOpen;
	}, [isOpen, onClose]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (event.target instanceof Element) {
				const isSelectInteraction = Boolean(
					event.target.closest(".listing-filter-select__menu") ||
					event.target.closest(".listing-filter-select__menu-portal") ||
					event.target.closest(".listing-filter-select__clear-indicator"),
				);

				if (isSelectInteraction) {
					return;
				}
			}

			if (
				containerRef.current &&
				event.target instanceof Node &&
				!containerRef.current.contains(event.target)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<div ref={containerRef} className={`relative ${className || ""}`}>
			<button
				type="button"
				onClick={() => setIsOpen((prev) => !prev)}
				className={`h-[42px] inline-flex items-center gap-2 rounded-lg border px-3.5 text-sm font-semibold transition-colors ${hasActiveFilters
					? "border-primary/40 bg-primary/10 text-primary"
					: "border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-gray-700"
					}`}
			>
				<Filter size={16} />
				{filterButtonLabel}
			</button>

			<div
				className={`listing-filter-panel ${enforceGrayBorders ? "listing-filter-gray-borders" : ""} absolute top-[calc(100%+8px)] z-40 w-[calc(100vw-2rem)] max-w-[420px] rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-800 shadow-xl transition-all duration-200 ${panelAlign === "right" ? "right-0 origin-top-right" : "left-0 origin-top-left"} ${isOpen
					? "opacity-100 translate-y-0 scale-100 pointer-events-auto"
					: "opacity-0 -translate-y-2 scale-95 pointer-events-none"
					}`}
			>
				<div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-3 py-2">
					<div className="inline-flex items-center gap-2 text-lg font-bold text-slate-900 dark:text-slate-100 leading-none">
						{panelTitle}
					</div>
					<button
						type="button"
						onClick={() => setIsOpen(false)}
						className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-gray-100 dark:hover:bg-gray-800"
						aria-label="Close filter panel"
					>
						<X size={16} />
					</button>
				</div>

				<div className="px-2 py-2">
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-1">
						{fields.map((field) => (
							<div key={field.id} className={`w-full min-w-0 max-w-full sm:max-w-[188px] mx-auto ${field.widthClass || ""}`}>
								{field.label && (
									<label className="mb-0.5 block text-xs font-semibold text-slate-700 dark:text-slate-200">
										{field.label}
										{field.isRequired && <span className="text-red-500 ml-1">*</span>}
									</label>
								)}

								{field.type === "text" && (
									<input
										type="text"
										value={field.value}
										placeholder={field.placeholder}
										disabled={field.disabled}
										onChange={(e) => field.onChange(e.target.value)}
										className={`${baseInputClassName} ${enforceGrayBorders ? "listing-filter-gray-border" : ""} ${field.errorMessage ? "border-red-500 dark:border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
									/>
								)}

								{field.type === "date" && (
									<div className="relative">
										<input
											type="date"
											value={field.value}
											min={field.min}
											max={field.max}
											placeholder={field.placeholder}
											disabled={field.disabled}
											onChange={(e) => field.onChange(e.target.value)}
											className={`${baseInputClassName} listing-filter-date-input ${!field.value ? "listing-filter-date-empty" : ""} pr-8 ${enforceGrayBorders ? "listing-filter-gray-border" : ""} ${field.errorMessage ? "border-red-500 dark:border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
										/>
										<span
											className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 ${field.disabled
												? "text-slate-400 dark:text-slate-500"
												: "text-slate-500 dark:text-slate-400"
												}`}
										>
											<Calendar size={13} />
										</span>
									</div>
								)}

								{field.type === "native-select" && (
									<div className="relative">
										<select
											value={field.value}
											disabled={field.disabled}
											onChange={(e) => field.onChange(e.target.value)}
											className={`${baseInputClassName} listing-filter-native-select pr-8 ${enforceGrayBorders ? "listing-filter-gray-border" : ""} ${field.errorMessage ? "border-red-500 dark:border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
											aria-label={field.label || field.placeholder || field.id}
										>
											{field.options.map((opt) => (
												<option key={`${field.id}-${opt.value}`} value={opt.value}>
													{opt.label}
												</option>
											))}
										</select>
										<span
											className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 ${field.disabled
												? "text-slate-400 dark:text-slate-500"
												: "text-slate-500 dark:text-slate-400"
												}`}
										>
											<ChevronDown size={13} />
										</span>
									</div>
								)}

								{field.type === "select" && (
									<Select
										placeholder={field.placeholder}
										options={field.options as CommonListingFilterOption[]}
										value={field.value as CommonListingFilterOption | null}
										isClearable={field.isClearable}
										isSearchable={field.isSearchable ?? true}
										isLoading={field.isLoading}
										onInputChange={field.onInputChange}
										onMenuScrollToBottom={field.onMenuScrollToBottom}
										onChange={(option) =>
											field.onChange((option as CommonFilterOption | null) || null)
										}
										className="text-sm"
										classNamePrefix="listing-filter-select"
										styles={selectStyles}
										menuPosition="fixed"
										menuPortalTarget={
											field.menuPortalTarget ||
											(typeof document !== "undefined" ? document.body : undefined)
										}
										noOptionsMessage={field.noOptionsMessage}
										isDisabled={field.disabled || field.isDisabled}
									/>
								)}

								{field.errorMessage && (
									<span className="mt-1 block text-[11px] font-medium text-red-500 dark:text-red-400">
										{field.errorMessage}
									</span>
								)}
							</div>
						))}
					</div>

					<div className="mt-2 flex items-center justify-center gap-2 border-t border-slate-200 dark:border-slate-700 pt-2">
						{onClear && (
							<button
								type="button"
								onClick={onClear}
								disabled={disableApplyOnEmpty && !hasActiveFilters}
								className={`rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-800 px-5 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 transition-colors duration-200 ${hasActiveFilters
									? "hover:bg-gray-50 dark:hover:bg-gray-700"
									: "opacity-50 cursor-not-allowed"
									}`}
							>
								Clear
							</button>
						)}

						{onReset && (
							<button
								type="button"
								onClick={() => {
									const result = onReset();
									if (result !== false) {
										setIsOpen(false);
									}
								}}
								disabled={!hasActiveFilters}
								className={`rounded-lg border border-red-300 dark:border-red-700 bg-white dark:bg-gray-800 px-5 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 transition-colors duration-200 ${hasActiveFilters
									? "hover:bg-red-50 dark:hover:bg-red-950/20"
									: "opacity-50 cursor-not-allowed"
									}`}
							>
								Reset
							</button>
						)}

						{onApply && (
							<button
								type="button"
								// Add the disabled attribute here to prevent click events
								disabled={disableApplyOnEmpty && !hasActiveFilters}
								onClick={() => {
									const result = onApply();
									if (result !== false) {
										setIsOpen(false);
									}
								}}
								className={`bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white border border-transparent rounded-lg px-5 py-2.5 text-sm font-medium transition-colors duration-200 ${(disableApplyOnEmpty && !hasActiveFilters) ? "opacity-50 cursor-not-allowed" : ""
									}`}
							>
								Apply
							</button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default CommonListingFilter;